/**
 * 并发任务执行器 - 基于 p-limit 的高性能实现
 *
 * 依赖: npm install p-limit
 */

const pLimit = require('p-limit');
const logger = require('../../logger');

class ConcurrentExecutor {
    /**
     * 执行并发任务
     * @param {Function[]} tasks - 任务函数数组
     * @param {number} concurrency - 并发数
     * @param {Object} options - 配置选项
     * @param {number} options.retries - 重试次数，默认0
     * @param {number} options.retryDelay - 重试延迟基数(ms)，默认1000
     * @param {boolean} options.failFast - 快速失败模式，默认true
     * @param {Function} options.onTaskComplete - 任务完成回调
     * @param {Function} options.onTaskError - 任务错误回调
     * @returns {Promise<Array>} 任务结果数组
     */
    static async run(tasks, concurrency, options = {}) {
        const { retries = 0, retryDelay = 1000, failFast = true, onTaskComplete, onTaskError } = options;

        const limit = pLimit(concurrency);

        const promises = tasks.map((task, index) =>
            limit(async () => {
                try {
                    const result = await this.executeTaskWithRetry(task, index, retries, retryDelay);

                    if (onTaskComplete) {
                        onTaskComplete(result, index);
                    }

                    return result;
                } catch (error) {
                    if (onTaskError) {
                        onTaskError(error, index);
                    }

                    logger.info(`[ConcurrentExecutor] Task ${index} failed:`, error.message);

                    if (failFast) {
                        throw error;
                    }

                    // 返回错误标记而不是抛出，让 Promise.allSettled 处理
                    return { error, index };
                }
            })
        );

        if (failFast) {
            // 快速失败模式：任何一个任务失败都会中止
            return Promise.all(promises);
        } else {
            // 容错模式：等待所有任务完成，收集成功和失败的结果
            const results = await Promise.allSettled(promises);

            const successes = [];
            const failures = [];

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    if (result.value && result.value.error) {
                        failures.push({ index, error: result.value.error });
                    } else {
                        successes.push({ index, result: result.value });
                    }
                } else {
                    failures.push({ index, error: result.reason });
                }
            });

            logger.info(`[ConcurrentExecutor] Completed: ${successes.length} success, ${failures.length} failed`);

            if (failures.length > 0) {
                const error = new Error(`${failures.length}/${tasks.length} tasks failed`);
                error.failures = failures;
                error.successes = successes;
                throw error;
            }

            return successes.map((s) => s.result);
        }
    }

    /**
     * 带重试的任务执行
     */
    static async executeTaskWithRetry(task, index, retries = 0, baseDelay = 1000) {
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await task();
            } catch (error) {
                if (attempt === retries) {
                    throw error;
                }

                const delay = baseDelay * Math.pow(2, attempt); // 指数退避
                logger.info(`[ConcurrentExecutor] Task ${index} retry ${attempt + 1}/${retries} after ${delay}ms`);

                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * 获取推荐的并发数（基于任务数量和系统资源）
     */
    static getRecommendedConcurrency(taskCount, defaultConcurrency = 10) {
        if (taskCount <= 5) return Math.min(taskCount, 3);
        if (taskCount <= 20) return Math.min(taskCount, 5);
        if (taskCount <= 100) return Math.min(taskCount, defaultConcurrency);

        // 大量任务时限制并发数，避免过载
        return Math.min(defaultConcurrency, 15);
    }
}

module.exports = ConcurrentExecutor;
